import { Fragment } from 'react';
import { Checkbox, Dropdown, Money, Text } from '@belong/ui';
import { clsx } from 'clsx';
import type {
  ConsumerDisputeReasonModel,
  ConsumerFailedPaymentModel,
} from '../failed-payments.types';
import { formatShortDate, getBalanceUniqueIds } from '../failed-payments-utils';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';

function findDisputeReason(
  failedPayment: ConsumerFailedPaymentModel,
  code: string,
): ConsumerDisputeReasonModel | undefined {
  return (failedPayment.disputeReasons ?? []).find(
    (reason) => reason.code === code,
  );
}

export function isDisputeReasonComplete(
  failedPayment: ConsumerFailedPaymentModel,
  code: string | undefined,
  note: string | undefined,
): boolean {
  if (!code) return false;
  const reason = findDisputeReason(failedPayment, code);
  if (!reason) return false;
  return !reason.requiresNote || Boolean(note?.trim());
}

type DisputeChargeSubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  disputeFullAmount: boolean | null;
  disputedBalanceUniqueIds: string[];
  disputeReasons: Record<string, string>;
  disputeReasonDetails: Record<string, string>;
  onSelectFullAmount: (value: boolean) => void;
  onToggleBalanceIds: (balanceUniqueIds: string[]) => void;
  onSelectReason: (balanceUniqueId: string, reason: string) => void;
  onChangeReasonDetail: (balanceUniqueId: string, detail: string) => void;
};

export function DisputeChargeSubsection({
  failedPayments,
  disputeFullAmount,
  disputedBalanceUniqueIds,
  disputeReasons,
  disputeReasonDetails,
  onSelectFullAmount,
  onToggleBalanceIds,
  onSelectReason,
  onChangeReasonDetail,
}: DisputeChargeSubsectionProps) {
  const totalAmount = failedPayments.reduce(
    (sum, failedPayment) => sum + Math.abs(failedPayment.amount ?? 0),
    0,
  );
  const disputedTotal = failedPayments.reduce((sum, failedPayment) => {
    const isSelected = getBalanceUniqueIds(failedPayment).some(
      (balanceUniqueId) => disputedBalanceUniqueIds.includes(balanceUniqueId),
    );
    return isSelected ? sum + Math.abs(failedPayment.amount ?? 0) : sum;
  }, 0);
  const stillToResolve = totalAmount - disputedTotal;

  const fullDisputeLabel =
    failedPayments.length === 1
      ? FAILED_PAYMENTS_STRINGS['dispute.total_full_single']
      : failedPayments.length === 2
        ? FAILED_PAYMENTS_STRINGS['dispute.total_full_pair']
        : FAILED_PAYMENTS_STRINGS['dispute.total_full_many'].replace(
            '{count}',
            String(failedPayments.length),
          );

  const showWarning =
    disputeFullAmount === true ||
    (disputeFullAmount === false && disputedBalanceUniqueIds.length > 0);

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-md">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['dispute.full_prompt']}
        </Text>
        <div className="grid grid-cols-1 gap-sm sm:grid-cols-2">
          {[true, false].map((value) => {
            const isSelected = disputeFullAmount === value;
            const label = value
              ? FAILED_PAYMENTS_STRINGS['withholding.full_payment_yes']
              : FAILED_PAYMENTS_STRINGS['withholding.full_payment_no'];

            return (
              <button
                key={String(value)}
                type="button"
                onClick={() => onSelectFullAmount(value)}
                className={clsx(
                  'flex h-[64px] w-full cursor-pointer items-center justify-center rounded border-0 px-md text-center shadow transition-colors',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <Text
                  variant="body"
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {label}
                </Text>
              </button>
            );
          })}
        </div>
      </div>

      {disputeFullAmount === true && (
        <div className="flex flex-col gap-xl">
          {failedPayments.map((failedPayment, index) => {
            const balanceUniqueId = getBalanceUniqueIds(failedPayment)[0];
            if (!balanceUniqueId) return null;
            const reason = disputeReasons[balanceUniqueId] ?? '';
            const detail = disputeReasonDetails[balanceUniqueId] ?? '';

            return (
              <Fragment key={balanceUniqueId}>
                {index > 0 ? (
                  <div className="border-0 border-t border-dashed border-gray" />
                ) : null}
                <div className="flex flex-col gap-xl">
                  <div className="flex items-center justify-between gap-md">
                    <Text
                      fontWeight="semibold"
                      className="min-w-0 flex-1 truncate text-navy"
                    >
                      {failedPayment.label ?? ''}
                    </Text>
                    <Money
                      value={Math.abs(failedPayment.amount ?? 0)}
                      format="DEFAULT"
                      className="shrink-0 text-body font-semibold text-navy"
                    />
                  </div>
                  <DisputeReasonPicker
                    failedPayment={failedPayment}
                    balanceUniqueId={balanceUniqueId}
                    reasonCode={reason}
                    note={detail}
                    onSelectReason={onSelectReason}
                    onChangeReasonDetail={onChangeReasonDetail}
                  />
                </div>
              </Fragment>
            );
          })}
          <div className="border-0 border-t border-dashed border-gray" />
          <div className="flex items-center justify-between">
            <Text fontWeight="semibold" className="text-navy">
              {fullDisputeLabel}
            </Text>
            <Money
              value={totalAmount}
              format="DEFAULT"
              className="text-body font-semibold text-navy"
            />
          </div>
        </div>
      )}

      {disputeFullAmount === false && (
        <div className="flex flex-col gap-sm">
          <Text variant="h3" fontWeight="semibold">
            {FAILED_PAYMENTS_STRINGS['dispute.which_title']}
          </Text>

          <div className="flex flex-col gap-xl">
            {failedPayments.map((failedPayment, index) => {
              const balanceIds = getBalanceUniqueIds(failedPayment);
              const balanceUniqueId = balanceIds[0];
              if (!balanceUniqueId) return null;

              const isSelected = balanceIds.some((id) =>
                disputedBalanceUniqueIds.includes(id),
              );

              return (
                <Fragment key={balanceUniqueId}>
                  {index > 0 ? (
                    <div className="border-0 border-t border-dashed border-gray" />
                  ) : null}
                  <div className="flex flex-col gap-xl">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onToggleBalanceIds(balanceIds)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onToggleBalanceIds(balanceIds);
                        }
                      }}
                      className="flex w-full cursor-pointer items-start justify-between gap-md text-left"
                    >
                      <span className="flex min-w-0 flex-1 flex-col gap-2xs overflow-hidden">
                        <span
                          role="presentation"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            id={`failed-payment-dispute-${balanceUniqueId}`}
                            checked={isSelected}
                            label={failedPayment.label ?? ''}
                            onChange={() => onToggleBalanceIds(balanceIds)}
                            wrapperClassName="min-w-0"
                            inputContainerClass="shrink-0"
                            labelClassName="block min-w-0 overflow-hidden"
                            labelTextClassName="truncate"
                          />
                        </span>
                        <Text
                          variant="p1"
                          fontWeight="semibold"
                          className="pl-[28px] text-red"
                        >
                          {FAILED_PAYMENTS_STRINGS['item.failed_prefix']}{' '}
                          {formatShortDate(failedPayment.failedOn)}
                        </Text>
                      </span>
                      <Money
                        value={-Math.abs(failedPayment.amount)}
                        format="DEFAULT"
                        className="shrink-0 text-body font-semibold"
                      />
                    </div>
                    {isSelected ? (
                      <DisputeReasonPicker
                        failedPayment={failedPayment}
                        balanceUniqueId={balanceUniqueId}
                        reasonCode={disputeReasons[balanceUniqueId] ?? ''}
                        note={disputeReasonDetails[balanceUniqueId] ?? ''}
                        onSelectReason={onSelectReason}
                        onChangeReasonDetail={onChangeReasonDetail}
                      />
                    ) : null}
                  </div>
                </Fragment>
              );
            })}
            <div className="border-0 border-t border-dashed border-gray" />
            <div className="flex items-center justify-between">
              <Text fontWeight="semibold" className="text-navy">
                {FAILED_PAYMENTS_STRINGS['dispute.still_to_resolve']}
              </Text>
              <Money
                value={stillToResolve}
                format="DEFAULT"
                className="text-body font-semibold text-navy"
              />
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-sm text-navy">
          <Text fontWeight="semibold">
            {FAILED_PAYMENTS_STRINGS['dispute.warning_title']}
          </Text>
          <Text variant="body">
            {FAILED_PAYMENTS_STRINGS['dispute.warning_body']}
          </Text>
        </div>
      )}
    </div>
  );
}

function DisputeReasonPicker({
  failedPayment,
  balanceUniqueId,
  reasonCode,
  note,
  onSelectReason,
  onChangeReasonDetail,
}: {
  failedPayment: ConsumerFailedPaymentModel;
  balanceUniqueId: string;
  reasonCode: string;
  note: string;
  onSelectReason: (balanceUniqueId: string, reason: string) => void;
  onChangeReasonDetail: (balanceUniqueId: string, detail: string) => void;
}) {
  const reasons = failedPayment.disputeReasons ?? [];
  const selected = findDisputeReason(failedPayment, reasonCode);

  return (
    <>
      <Dropdown
        id={`failed-payment-dispute-reason-${balanceUniqueId}`}
        name={`failed-payment-dispute-reason-${balanceUniqueId}`}
        value={reasonCode || undefined}
        label={FAILED_PAYMENTS_STRINGS['dispute.reason_label']}
        options={reasons.map((reason) => ({
          label: reason.label,
          value: reason.code,
        }))}
        onChange={(value) => onSelectReason(balanceUniqueId, value)}
      />
      {selected?.requiresNote ? (
        <input
          type="text"
          value={note}
          onChange={(event) =>
            onChangeReasonDetail(balanceUniqueId, event.target.value)
          }
          placeholder={FAILED_PAYMENTS_STRINGS['dispute.describe_reason']}
          className="w-full border-0 border-b border-solid border-gray bg-transparent pb-2xs font-sans text-body text-navy placeholder:text-dark-gray"
        />
      ) : null}
    </>
  );
}
